import * as React from 'react';
import { ApiHooksCaching } from './caching';
import { ApiHooksStore } from './store';
import { ApiHooksSystemSettings } from './systemSettings';
/**
 * API Hooks - Responders
 * ----------------
 * A system for advanced cache management.
 *
 * This file contains everything relating to the responder library, includes:
 * - React provider
 * - Responder types
 * - Responder factory
 * - Responder tools and methods
 */
export var ApiHooksResponders;
(function (ApiHooksResponders) {
    /** the three sets of listeners exported and ready to be called from within APIHooks.  */
    ApiHooksResponders.registeredQueryListeners = [];
    ApiHooksResponders.registeredRequestListeners = [];
    ApiHooksResponders.registeredMutationListeners = [];
    /**
     * tn effect allowing listeners to be registered and un-registered, used by responders.
     * @param endpointHash the `controller.endpoint` hash of the endpoint in question.
     * @param callback the function to call when the fetch is complete.
     * @param listenerArray The constant array in which to register the listener.
     */
    function useListenerEffect(endpointHash, callback, listenerArray) {
        React.useEffect(() => {
            // register the incoming listener
            listenerArray.push({ endpointHash, callback });
            return () => {
                // remove the outgoing listener
                const outgoingListeners = listenerArray.filter((rl) => rl.endpointHash === endpointHash && rl.callback === callback);
                for (const outgoingListener of outgoingListeners) {
                    listenerArray.splice(listenerArray.indexOf(outgoingListener), 1);
                }
            };
        }, [endpointHash, callback]);
    }
    /** the factory class created by the consuming application. allows responders to be created in a type safe way */
    class Factory {
        /** the root `use` function allows the consuming app to create responders. */
        use(responder) {
            /** A new responder hook is created */
            const responderHook = (state, dispatch) => {
                /** a memo of tools to pass back to the new responder */
                const params = React.useMemo(() => ({
                    useListener: (controllerKey, endpointKey) => {
                        const endpointHash = `${controllerKey}.${endpointKey}`;
                        return {
                            query: (callback) => {
                                useListenerEffect(endpointHash, callback, ApiHooksResponders.registeredQueryListeners);
                            },
                            mutation: (callback) => {
                                useListenerEffect(endpointHash, callback, ApiHooksResponders.registeredMutationListeners);
                            },
                            request: (callback) => {
                                useListenerEffect(endpointHash, callback, ApiHooksResponders.registeredRequestListeners);
                            },
                        };
                    },
                    getCache: (controllerKey, endpointKey, incomingCacheKey) => {
                        var _a, _b;
                        const endpointHash = `${controllerKey}.${endpointKey}`;
                        const cacheKey = incomingCacheKey !== null && incomingCacheKey !== void 0 ? incomingCacheKey : ApiHooksCaching.defaultCacheKey;
                        return (_b = (_a = state === null || state === void 0 ? void 0 : state[endpointHash]) === null || _a === void 0 ? void 0 : _a[cacheKey]) === null || _b === void 0 ? void 0 : _b.data;
                    },
                    setCache: (controllerKey, endpointKey, incomingData, incomingCacheKey, incomingParams) => {
                        var _a, _b, _c;
                        const endpointHash = `${controllerKey}.${endpointKey}`;
                        const cacheKey = incomingCacheKey !== null && incomingCacheKey !== void 0 ? incomingCacheKey : ApiHooksCaching.defaultCacheKey;
                        const existingSlice = (_a = state === null || state === void 0 ? void 0 : state[endpointHash]) === null || _a === void 0 ? void 0 : _a[cacheKey];
                        dispatch === null || dispatch === void 0 ? void 0 : dispatch(ApiHooksStore.Actions.loaded(endpointHash, incomingParams ? ApiHooksCaching.hashParams(incomingParams) : (_b = existingSlice === null || existingSlice === void 0 ? void 0 : existingSlice.paramHash) !== null && _b !== void 0 ? _b : '', cacheKey, incomingData, (_c = existingSlice === null || existingSlice === void 0 ? void 0 : existingSlice.maxCachingDepth) !== null && _c !== void 0 ? _c : ApiHooksSystemSettings.systemDefaultQuery.maxCachingDepth));
                    },
                }), [state, dispatch]);
                return responder(params);
            };
            return responderHook;
        }
    }
    /** the root factory creator, receives the API Client to derive it's type only, es-lint disagrees with this approach but I like it. */
    // eslint-disable-next-line
    function createFactory(apiClient) {
        return new Factory();
    }
    ApiHooksResponders.createFactory = createFactory;
    /**
     * this component isn't strictly a provider, it simply adds the responder hooks passed to it to the React environment
     * NOTE: this must be used INSIDE an APIHooks store provider.
     * FURTHER NOTE: armstrong users - please add this component INSIDE armstrong, (the APIHooks store provider should always be OUTSIDE armstrong)
     * */
    ApiHooksResponders.Provider = ({ children, responders }) => {
        const [state, dispatch] = React.useContext(ApiHooksStore.Context);
        responders.forEach((responderHook) => responderHook(state, dispatch));
        return React.createElement(React.Fragment, null, children);
    };
})(ApiHooksResponders || (ApiHooksResponders = {}));
